/**
 * SEO helpers: canonical host, canonical URL, hreflang alternates.
 *
 * Single source of truth for absolute front URLs. Drupal metatags point to the
 * backend host and may carry request query strings; everything rendered in
 * <head> must go through these helpers.
 */

const projectConfig = require("../project.config")

// Languages actually translated and published. Other Drupal languages
// (Tfng, de, nl, es, it) must never be advertised to search engines.
export const PUBLISHED_LANGUAGES = ["fr", "ar", "en"]
export const X_DEFAULT_LANGUAGE = "fr"

// Every language prefix the router knows, published or not.
const KNOWN_LANGUAGES = projectConfig?.languages?.enabled || PUBLISHED_LANGUAGES

// Former brand / campaign domains: still referenced in some Drupal metatags
// (og:image) and in old backlinks. They no longer serve the site.
export const LEGACY_HOSTS = [
	"attijaribilahoudoud.com",
	"www.attijaribilahoudoud.com",
	"dimainvestmaroc.com",
	"www.dimainvestmaroc.com",
]

// Public hosts of the Drupal backend, as written by Drupal in its metatags.
// NEXT_PUBLIC_DRUPAL_BASE_URL may point to an internal address (docker,
// reverse proxy) that differs from the host Drupal emits.
const BACKEND_PUBLIC_HOSTS = [
	"backend.attijarimdm.com",
	"backend-mdm.leserveurdetest.com",
]

// Hostnames that must not be forced to www (local dev, preprod, previews).
const NO_WWW_HOSTNAME_PATTERNS = [
	/^localhost$/,
	/^\[::1\]$/,
	/^\d{1,3}(\.\d{1,3}){3}$/,
	/(^|\.)ondocker\.com$/,
	/(^|\.)onecloud\.ma$/,
]

const PLACEHOLDER_BASE = "http://placeholder.invalid"

const parseUrl = (value, base) => {
	try {
		return new URL(value, base)
	} catch {
		return null
	}
}

/**
 * Whether a host (port allowed) must be redirected to / advertised as its www
 * variant.
 */
export const shouldForceWww = (host) => {
	const hostname = host ? parseUrl(`http://${host}`)?.hostname : null
	if (!hostname || hostname.startsWith("www.")) {
		return false
	}
	return !NO_WWW_HOSTNAME_PATTERNS.some((pattern) => pattern.test(hostname))
}

/**
 * Public origin of the front, without trailing slash, e.g.
 * https://www.attijarimdm.com. Mirrors the non-www → www redirect of proxy.js
 * so canonical URLs never point to a redirecting host. Empty string when the
 * base URL is missing or invalid: callers must then render no absolute URL.
 */
export const getSiteOrigin = (baseUrl = process.env.NEXT_PUBLIC_BASE_URL) => {
	const url = baseUrl ? parseUrl(baseUrl) : null
	if (!url) {
		return ""
	}
	if (shouldForceWww(url.host)) {
		url.hostname = `www.${url.hostname}`
	}
	return url.origin
}

/**
 * Whether an absolute URL belongs to the site (front host with or without www,
 * backend, former brand domain) and must therefore be expressed on the front
 * origin.
 */
const isSiteUrl = (url, { origin, backendBase }) => {
	const siteHost = parseUrl(origin)?.host
	const backendHost = backendBase ? parseUrl(backendBase)?.host : null
	return (
		url.host === siteHost ||
		`www.${url.host}` === siteHost ||
		(Boolean(backendHost) && url.host === backendHost) ||
		BACKEND_PUBLIC_HOSTS.includes(url.host) ||
		LEGACY_HOSTS.includes(url.host)
	)
}

/**
 * Rewrites an absolute URL of the site (backend, bare host, legacy domain)
 * onto the front origin. Other values are returned untouched (relative paths,
 * third-party URLs, plain text).
 */
export const rewriteToSiteOrigin = (
	value,
	{ backendBase = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL, origin = getSiteOrigin() } = {}
) => {
	if (typeof value !== "string" || !origin || !/^https?:\/\//i.test(value)) {
		return value
	}
	const url = parseUrl(value)
	if (!url || !isSiteUrl(url, { origin, backendBase })) {
		return value
	}
	return `${origin}${url.pathname}${url.search}${url.hash}`
}

const normalizePathname = (pathname) => {
	if (!pathname || pathname === "/") {
		return "/"
	}
	return pathname.replace(/\/+$/, "")
}

// Front listings are paginated from 1: page 1 (or a missing/invalid value)
// is the bare URL, pages 2+ are distinct indexable URLs.
const normalizePageParam = (value) => {
	if (!/^\d+$/.test(value || "")) {
		return null
	}
	const page = parseInt(value, 10)
	return page > 1 ? String(page) : null
}

/**
 * Turns a path or an absolute URL into the absolute canonical URL: site URLs
 * are moved onto the front origin, without query string and fragment. With
 * keepPage, ?page=N (N > 1) is kept: only paginated listings use it. A
 * deliberate external canonical (another domain) is kept on its own host.
 * Returns null without a usable origin.
 */
export const toCanonicalUrl = (
	pathOrUrl,
	origin = getSiteOrigin(),
	{ backendBase = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL, keepPage = false } = {}
) => {
	if (!pathOrUrl || !origin) {
		return null
	}
	const url = parseUrl(pathOrUrl, PLACEHOLDER_BASE)
	if (!url) {
		return null
	}
	const isRelative = url.origin === PLACEHOLDER_BASE
	if (!isRelative && !isSiteUrl(url, { origin, backendBase })) {
		return `${url.origin}${normalizePathname(url.pathname)}`
	}
	const page = keepPage ? normalizePageParam(url.searchParams.get("page")) : null
	return `${origin}${normalizePathname(url.pathname)}${page ? `?page=${page}` : ""}`
}

const pathnameOf = (pathOrUrl) =>
	pathOrUrl ? parseUrl(pathOrUrl, PLACEHOLDER_BASE)?.pathname || null : null

const getLanguagePrefix = (pathname) => {
	const [, firstSegment] = (pathname || "").split("/")
	return KNOWN_LANGUAGES.includes(firstSegment) ? firstSegment : null
}

// Drupal falls back to /{lang}/node/{nid} when a translation has no alias:
// in practice the content is not translated in that language.
const isUntranslatedFallback = (path) => {
	const pathname = pathnameOf(path)
	return Boolean(getLanguagePrefix(pathname)) && /^\/[^/]+\/node\/\d+\/?$/.test(pathname)
}

const isHomePathname = (pathname) => {
	const normalized = normalizePathname(pathname)
	return normalized === "/" || normalized === `/${getLanguagePrefix(normalized)}`
}

/**
 * Whether a path or absolute URL is a language home (/, /fr, /ar…).
 */
export const isHomeUrl = (pathOrUrl) => {
	const pathname = pathnameOf(pathOrUrl)
	return pathname ? isHomePathname(pathname) : false
}

/**
 * Whether the current node is the home page: Drupal canonical on a language
 * root, or translation of the current locale equal to /{locale}.
 */
export const isHomePage = ({ drupalCanonical, translations, locale }) =>
	isHomeUrl(drupalCanonical) ||
	(Boolean(locale) && translations?.[locale] === `/${locale}`)

/**
 * Canonical URL of the current page.
 *
 * - Filtered listing routes: the requested path itself (with ?page=N, N > 1).
 * - Drupal canonical path on the front origin, without query string.
 * - Home page: Drupal returns the default-language site URL for every
 *   language (/en canonicalized to /fr); the canonical is /{locale}.
 * - Drupal canonical in another language than the current one: translation
 *   of the current locale.
 */
export const getCanonicalUrl = ({
	drupalCanonical,
	locale,
	translations,
	origin,
	listingPath,
}) => {
	const siteOrigin = origin ?? getSiteOrigin()
	const pages = translations ?? {}
	if (!siteOrigin) {
		return null
	}
	// Filtered / paginated listing (/je-veux-un-conseil/{theme}?page=2): each
	// filter and page is its own content, the canonical is the page itself.
	if (listingPath) {
		return toCanonicalUrl(listingPath, siteOrigin, { keepPage: true })
	}
	if (locale && isHomePage({ drupalCanonical, translations: pages, locale })) {
		return `${siteOrigin}/${locale}`
	}
	const canonicalPath = pathnameOf(drupalCanonical)
	if (!canonicalPath) {
		return pages[locale] ? toCanonicalUrl(pages[locale], siteOrigin) : null
	}
	const canonicalLanguage = getLanguagePrefix(canonicalPath)
	if (
		locale &&
		canonicalLanguage &&
		canonicalLanguage !== locale &&
		pages[locale] &&
		!isUntranslatedFallback(pages[locale])
	) {
		return toCanonicalUrl(pages[locale], siteOrigin)
	}
	return toCanonicalUrl(drupalCanonical, siteOrigin)
}

/**
 * hreflang alternates for the published languages that have a real
 * translation, plus x-default.
 *
 * Returns [] when fewer than two languages exist, and when the page is not
 * part of its own cluster (canonical different from its own alternate, e.g.
 * ?page=2 or a canonical overridden in Drupal): Google ignores, and reports,
 * clusters without a return link.
 */
export const getHreflangAlternates = ({
	translations,
	isHome = false,
	origin,
	locale,
	canonicalUrl,
}) => {
	const siteOrigin = origin ?? getSiteOrigin()
	const pages = translations ?? {}
	if (!siteOrigin) {
		return []
	}
	const alternates = PUBLISHED_LANGUAGES.map((lang) => {
		if (isHome) {
			return { hrefLang: lang, href: `${siteOrigin}/${lang}` }
		}
		const path = pages[lang]
		if (!path || isUntranslatedFallback(path)) {
			return null
		}
		return { hrefLang: lang, href: toCanonicalUrl(path, siteOrigin) }
	}).filter(Boolean)

	if (alternates.length < 2) {
		return []
	}
	if (locale && canonicalUrl) {
		const self = alternates.find((alternate) => alternate.hrefLang === locale)
		if (!self || self.href !== canonicalUrl) {
			return []
		}
	}
	const xDefault =
		alternates.find((alternate) => alternate.hrefLang === X_DEFAULT_LANGUAGE) ||
		alternates[0]
	return [...alternates, { hrefLang: "x-default", href: xDefault.href }]
}

/**
 * Path to use as canonical for a filtered listing route, or null.
 *
 * Filtered listings (/je-veux-un-conseil/{theme}, /contacts-utiles/{type}/
 * {pays}) carry their filter values in systemRoute._query; each filter and
 * page is its own content. Exception: the first page with every filter on
 * "all" is the listing node itself (Drupal canonical).
 */
export const getListingPath = ({ systemRoute, locale, asPath }) => {
	const filters = systemRoute?._query
	if (!filters || typeof filters !== "object" || !locale || !asPath) {
		return null
	}
	const values = Object.values(filters).map((value) =>
		`${value ?? ""}`.trim().toLowerCase()
	)
	const page = normalizePageParam(
		new URLSearchParams(asPath.split("?")[1] || "").get("page")
	)
	if (values.length && values.every((value) => value === "all") && !page) {
		return null
	}
	return `/${locale}${asPath.startsWith("/") ? asPath : `/${asPath}`}`
}
